'use client';

import { c as _c } from "react/compiler-runtime";
import { useActiveWallet, useBoostTrade, useTurnkeySigningDeps } from '@morfi/backend-sdk-v2/client';
import { useTranslations } from 'next-intl';
import { useEffect, useState } from 'react';
import { AnimatedAmountInput } from '@/components/trade/animated-amount-input';
import { AnimatedButton } from '@/components/ui/animated-button';
import { SilkSheet } from '@/components/ui/silk-sheet';
import { onEvent } from '@/events';
import { useAuthGate } from '@/hooks/use-auth-gate';
import { useFormattedCurrency, useQuickAmounts } from '@/lib/currency';
import { useWalletBalances } from '@/queries/wallet-balance';

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface BoostDialogProps {
  marketPubkey: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const MIN_BOOST_AMOUNT = 1;

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function BoostDialog(t0) {
  const $ = _c(76);
  const {
    marketPubkey,
    open,
    onOpenChange
  } = t0;
  const t = useTranslations("market");
  const {
    formatBalance,
    currencySymbol,
    toUsdc,
    exchangeRate
  } = useFormattedCurrency();
  const {
    activeWallet
  } = useActiveWallet();
  const {
    openLogin
  } = useAuthGate();
  const activeWalletAddress = activeWallet?.address;
  let t1;
  if ($[0] !== activeWalletAddress) {
    t1 = activeWalletAddress !== undefined ? [activeWalletAddress] : [];
    $[0] = activeWalletAddress;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const {
    perWallet: balancePerWallet
  } = useWalletBalances(t1);
  const boostMutation = useBoostTrade();
  const isConnected = activeWallet !== null && activeWallet !== undefined;
  const signingDeps = useTurnkeySigningDeps();
  const [amount, setAmount] = useState("");
  const balance = activeWalletAddress !== undefined ? balancePerWallet.get(activeWalletAddress) ?? 0 : 0;
  let t2;
  let usdcAmount;
  if ($[2] !== amount || $[3] !== toUsdc) {
    const parsedLocalAmount = Number.parseFloat(amount);
    usdcAmount = toUsdc(parsedLocalAmount);
    t2 = !Number.isNaN(parsedLocalAmount) && usdcAmount >= MIN_BOOST_AMOUNT;
    $[2] = amount;
    $[3] = toUsdc;
    $[4] = t2;
    $[5] = usdcAmount;
  } else {
    t2 = $[4];
    usdcAmount = $[5];
  }
  const isValidNumber = t2;
  const exceedsBalance = isValidNumber && usdcAmount > balance;
  const isValid = isValidNumber && !exceedsBalance;
  const isSubmitting = boostMutation.isPending;
  const balanceInDisplayCurrency = balance * exchangeRate;
  let t3;
  if ($[6] !== balanceInDisplayCurrency) {
    t3 = {
      maxLocal: balanceInDisplayCurrency
    };
    $[6] = balanceInDisplayCurrency;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const quickAmounts = useQuickAmounts(t3);
  let t4;
  let t5;
  if ($[8] !== marketPubkey || $[9] !== open) {
    t4 = () => {
      if (open) {
        onEvent("boost_initiated", {
          marketPubkey,
          amountUsdc: 0
        });
      }
    };
    t5 = [open, marketPubkey];
    $[8] = marketPubkey;
    $[9] = open;
    $[10] = t4;
    $[11] = t5;
  } else {
    t4 = $[10];
    t5 = $[11];
  }
  useEffect(t4, t5);
  let t6;
  if ($[12] !== boostMutation || $[13] !== open) {
    t6 = () => {
      if (!open) {
        setAmount("");
        boostMutation.reset();
      }
    };
    $[12] = boostMutation;
    $[13] = open;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  let t7;
  if ($[15] !== boostMutation.reset || $[16] !== open) {
    t7 = [open, boostMutation.reset];
    $[15] = boostMutation.reset;
    $[16] = open;
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  useEffect(t6, t7);
  let t8;
  let t9;
  if ($[18] !== boostMutation.data || $[19] !== boostMutation.isSuccess || $[20] !== marketPubkey || $[21] !== onOpenChange || $[22] !== usdcAmount) {
    t8 = () => {
      if (boostMutation.isSuccess && boostMutation.data?.ok === true) {
        onEvent("boost_completed", {
          marketPubkey,
          amountUsdc: usdcAmount,
          signature: boostMutation.data.signature
        });
        onOpenChange(false);
      }
    };
    t9 = [boostMutation.isSuccess, boostMutation.data, marketPubkey, usdcAmount, onOpenChange];
    $[18] = boostMutation.data;
    $[19] = boostMutation.isSuccess;
    $[20] = marketPubkey;
    $[21] = onOpenChange;
    $[22] = usdcAmount;
    $[23] = t8;
    $[24] = t9;
  } else {
    t8 = $[23];
    t9 = $[24];
  }
  useEffect(t8, t9);
  let t10;
  if ($[25] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = function handleQuickAmount(quickAmount) {
      setAmount(String(quickAmount));
    };
    $[25] = t10;
  } else {
    t10 = $[25];
  }
  const handleQuickAmount = t10;
  let t11;
  if ($[26] !== balanceInDisplayCurrency) {
    t11 = function handleMaxClick() {
      const maxLocal = Math.floor(balanceInDisplayCurrency * 100) / 100;
      setAmount(String(Math.max(0, maxLocal)));
    };
    $[26] = balanceInDisplayCurrency;
    $[27] = t11;
  } else {
    t11 = $[27];
  }
  const handleMaxClick = t11;
  let t12;
  if ($[28] !== activeWallet || $[29] !== boostMutation || $[30] !== isSubmitting || $[31] !== isValid || $[32] !== marketPubkey || $[33] !== signingDeps || $[34] !== usdcAmount) {
    t12 = function handleConfirm() {
      if (!isValid || isSubmitting || !activeWallet) {
        return;
      }
      boostMutation.mutate({
        marketAddress: marketPubkey,
        boostAmountUsdc: usdcAmount,
        activeWallet: {
          id: activeWallet.id,
          address: activeWallet.address,
          type: activeWallet.type
        },
        signingDeps
      });
    };
    $[28] = activeWallet;
    $[29] = boostMutation;
    $[30] = isSubmitting;
    $[31] = isValid;
    $[32] = marketPubkey;
    $[33] = signingDeps;
    $[34] = usdcAmount;
    $[35] = t12;
  } else {
    t12 = $[35];
  }
  const handleConfirm = t12;
  const T0 = SilkSheet;
  const t13 = "detached";
  const t14 = "md";
  const t15 = true;
  let t16;
  if ($[36] !== t) {
    t16 = t("boost.dialogTitle");
    $[36] = t;
    $[37] = t16;
  } else {
    t16 = $[37];
  }
  let t17;
  if ($[38] !== t) {
    t17 = t("boost.description");
    $[38] = t;
    $[39] = t17;
  } else {
    t17 = $[39];
  }
  const t18 = "flex items-center justify-between gap-3";
  const t19 = "text-caption text-foreground-secondary";
  const t20 = t("boost.balance", {
    amount: formatBalance(balance)
  });
  let t21;
  if ($[40] !== t20) {
    t21 = <span className={t19}>{t20}</span>;
    $[40] = t20;
    $[41] = t21;
  } else {
    t21 = $[41];
  }
  let t22;
  if ($[42] !== exceedsBalance || $[43] !== handleConfirm || $[44] !== isConnected || $[45] !== isSubmitting || $[46] !== isValid || $[47] !== onOpenChange || $[48] !== openLogin || $[49] !== t) {
    t22 = isConnected ? <AnimatedButton disabled={!isValid || isSubmitting} loading={isSubmitting} onClick={handleConfirm}>{exceedsBalance ? t("boost.insufficientBalance") : t("boost.confirmButton")}</AnimatedButton> : <AnimatedButton onClick={() => {
      onOpenChange(false);
      openLogin();
    }}>{t("boost.connectWallet")}</AnimatedButton>;
    $[42] = exceedsBalance;
    $[43] = handleConfirm;
    $[44] = isConnected;
    $[45] = isSubmitting;
    $[46] = isValid;
    $[47] = onOpenChange;
    $[48] = openLogin;
    $[49] = t;
    $[50] = t22;
  } else {
    t22 = $[50];
  }
  let t23;
  if ($[51] !== t21 || $[52] !== t22) {
    t23 = <div className={t18}>{t21}{t22}</div>;
    $[51] = t21;
    $[52] = t22;
    $[53] = t23;
  } else {
    t23 = $[53];
  }
  const t24 = !isValid || isSubmitting ? undefined : handleConfirm;
  let t25;
  if ($[54] !== t) {
    t25 = t("boost.amountInputLabel");
    $[54] = t;
    $[55] = t25;
  } else {
    t25 = $[55];
  }
  let t26;
  if ($[56] !== t) {
    t26 = t("boost.maxButton");
    $[56] = t;
    $[57] = t26;
  } else {
    t26 = $[57];
  }
  let t27;
  if ($[58] !== amount || $[59] !== currencySymbol || $[60] !== handleMaxClick || $[61] !== handleQuickAmount || $[62] !== isSubmitting || $[63] !== quickAmounts || $[64] !== t24 || $[65] !== t25 || $[66] !== t26) {
    t27 = <AnimatedAmountInput value={amount} onChange={setAmount} currencySymbol={currencySymbol} quickAmounts={quickAmounts} onQuickAmount={handleQuickAmount} onMaxClick={handleMaxClick} onSubmit={t24} disabled={isSubmitting} inputLabel={t25} maxLabel={t26} />;
    $[58] = amount;
    $[59] = currencySymbol;
    $[60] = handleMaxClick;
    $[61] = handleQuickAmount;
    $[62] = isSubmitting;
    $[63] = quickAmounts;
    $[64] = t24;
    $[65] = t25;
    $[66] = t26;
    $[67] = t27;
  } else {
    t27 = $[67];
  }
  let t28;
  if ($[68] !== T0 || $[69] !== onOpenChange || $[70] !== open || $[71] !== t16 || $[72] !== t17 || $[73] !== t23 || $[74] !== t27) {
    t28 = <T0 open={open} onOpenChange={onOpenChange} variant={t13} size={t14} keyboard={t15} title={t16} description={t17} footer={t23}>{t27}</T0>;
    $[68] = T0;
    $[69] = onOpenChange;
    $[70] = open;
    $[71] = t16;
    $[72] = t17;
    $[73] = t23;
    $[74] = t27;
    $[75] = t28;
  } else {
    t28 = $[75];
  }
  return t28;
}